import { useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

export const useEmail = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const fetchEmails = async (filters = {}, page = 1, limit = 20) => {
    return new Promise(async (resolve, reject) => {
      try {
        const queryParams = new URLSearchParams();

        // Handle spam filter
        if (filters.onlySpam !== undefined) {
          queryParams.append('onlySpam', filters.onlySpam.toString());
        }

        // Handle replied filter
        if (filters.includeRepliedTo !== undefined) {
          queryParams.append('includeRepliedTo', filters.includeRepliedTo.toString());
        }

        // Add other filters
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && key !== 'onlySpam' && key !== 'includeRepliedTo') {
            queryParams.append(key, value.toString());
          }
        });

        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());

        const responseData = await sendRequest({
          url: `/api/v1/mail?${queryParams.toString()}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const fetchEmailThread = async (emailId, options = {}) => {
    return new Promise(async (resolve, reject) => {
      try {
        const queryParams = new URLSearchParams();
        if (options.skipAiSuggestion) {
          queryParams.append('skipAiSuggestion', 'true');
        }
        if (options.forceAiSuggestion) {
          queryParams.append('forceAiSuggestion', 'true');
        }
        const queryString = queryParams.toString();
        const endpoint = queryString
          ? `/api/v1/mail/${emailId}/thread?${queryString}`
          : `/api/v1/mail/${emailId}/thread`;

        const responseData = await sendRequest({
          url: endpoint,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getMailAiSuggestion = async (mailId, options = {}) => {
    return new Promise(async (resolve, reject) => {
      try {
        const queryParams = new URLSearchParams();
        if (options.force) {
          queryParams.append('force', 'true');
        }
        const queryString = queryParams.toString();
        const endpoint = queryString
          ? `/api/v1/mail/${mailId}/ai-suggestion?${queryString}`
          : `/api/v1/mail/${mailId}/ai-suggestion`;

        const responseData = await sendRequest({
          url: endpoint,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const downloadMailAttachment = async (mailId, attachmentId) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/attachments/${attachmentId}/download`,
          method: 'GET',
          returnBlob: true,
          headers: {
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const sendEmail = async email => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: JSON.stringify(email),
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const sendReply = async (mailId, content, attachments = []) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/reply`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: {
            textBody: content,
            htmlBody: content.replace(/\n/g, '<br>'),
            attachments: attachments,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const fetchEmailsByInternalBookingId = async internalBookingId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/internal-booking/${internalBookingId}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const fetchEmailsByInvoiceId = async invoiceId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/invoice/${invoiceId}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const assignMail = async (mailId, assignToCurrentUser = true, users = null) => {
    return new Promise(async (resolve, reject) => {
      try {
        const body = assignToCurrentUser ? { assignToCurrentUser: true } : { users };
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/assign/users`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: body,
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const assignGroups = async (mailId, groups, filters) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/assign/groups`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: { groups, filters },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getMailDrafts = async mailId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/drafts`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const setMailDraft = async (mailId, text) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/drafts`,
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: { text },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const setMailComment = async (mailId, comment) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/comment`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: { comment },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const assignBranch = async (mailId, branchId) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/assign/branch`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: { branch: branchId },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const assignEntertainment = async (mailId, entertainmentId, filters) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/assign/entertainment`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: { entertainment: entertainmentId, filters },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const updateSpamStatus = async (mailId, isSpam) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/spam`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: { isSpam },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getMailCounts = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/mail/counts',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getLiveDraft = async mailId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/drafts/live`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const markMailAsDone = async mailId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/done`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const updateMailStatus = async (mailId, status) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/status`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: { status },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const runAiMailAction = async (mailId, payload) => {
    return new Promise(async (resolve, reject) => {
      try {
        const { toolName, tool, args } = payload || {};
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/ai-actions/run`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: {
            toolName: toolName || tool,
            args,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Fetch the available sending identities for the compose dropdown
  const getSenderIdentities = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/mail/identities',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Compose and send a brand-new email to arbitrary recipients
  const composeEmail = async payload => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/mail/compose',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: payload,
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // List the current user's private compose drafts (without attachment content)
  const getComposeDrafts = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/mail/compose/drafts',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Load a single compose draft with attachment content for editing
  const getComposeDraft = async draftId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/compose/drafts/${draftId}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Create or update a compose draft. When draftId is provided the draft is updated.
  const saveComposeDraft = async (payload, draftId = null) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: draftId
            ? `/api/v1/mail/compose/drafts/${draftId}`
            : '/api/v1/mail/compose/drafts',
          method: draftId ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: payload,
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Delete a compose draft
  const deleteComposeDraft = async draftId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/compose/drafts/${draftId}`,
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Poll the live delivery status of a sent mail (compose feedback step)
  const getMailDeliveryStatus = async mailId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/mail/${mailId}/delivery-status`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  return {
    isLoading,
    error,
    fetchEmails,
    fetchEmailThread,
    sendEmail,
    sendReply,
    getSenderIdentities,
    composeEmail,
    getComposeDrafts,
    getComposeDraft,
    saveComposeDraft,
    deleteComposeDraft,
    getMailDeliveryStatus,
    fetchEmailsByInternalBookingId,
    fetchEmailsByInvoiceId,
    assignMail,
    assignGroups,
    getMailDrafts,
    setMailDraft,
    setMailComment,
    assignBranch,
    assignEntertainment,
    updateSpamStatus,
    getMailCounts,
    getLiveDraft,
    markMailAsDone,
    updateMailStatus,
    runAiMailAction,
    getMailAiSuggestion,
    downloadMailAttachment,
  };
};
